import { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './InputRow.module.css';

import { useAppSelector } from '../../../../hooks/useRedux';
import { useSlice } from '../../../../hooks/useSlice';

import Input from '../../../Input';
import TipButton from '../../../TipButton/TipButton';
import {
  BROWSE,
  COVER,
  DEFAULT_COVER_IMAGE,
  ENTER_AUTHOR,
  ENTER_PLACEHOLDER,
  ENTER_PUBLISHER,
  NEW,
  ORIGINAL,
} from '../../../../constants';
import { formatDate } from '../../../../utils/date';

import { Controller, useFormContext } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { isOfType } from '../../../../utils/typeGuard';
import { TooltipWrapper } from '../../../Tooltip';

interface InputRow {
  name: string;
  title: string;
}

export default function InputRow({ name, title }: InputRow) {
  const cx = classNames.bind(styles);
  const { i18n } = useTranslation();
  const { selectSingleItem, selectSelectedItem } = useSlice();
  const singleItem = useAppSelector(selectSingleItem);
  const selectedItem = useAppSelector(selectSelectedItem);
  const [isNewCover, setIsNewCover] = useState(
    isOfType(singleItem, 'project') &&
      isOfType(singleItem.project, 'orgCoverYn') &&
      !singleItem.project.orgCoverYn
  );
  const [coverFilename, setCoverFilename] = useState(DEFAULT_COVER_IMAGE);
  const [hasProfanity, setHasProfanity] = useState(false);

  const { control, setValue, getValues } = useFormContext();

  const inputValue =
    name.includes('Date') && selectedItem?.project[name]
      ? formatDate(`${selectedItem?.project[name]}`)
      : name.includes('copyright')
      ? selectedItem?.projectCopyright[name]
      : selectedItem?.project[name];
  const isStartPage = name === 'startPage';
  const isPubDate = name === 'pubDate';
  const extraInputClass = isStartPage ? 'tac' : '';
  const value =
    name === 'pubDate' && !inputValue
      ? selectedItem?.projectResource?.regDate
      : inputValue;
  const placeholderMap: Record<string, string> = {
    startPage: '1',
    publisher: ENTER_PUBLISHER,
    author: ENTER_AUTHOR,
  };

  const placeholder = placeholderMap[name] || ENTER_PLACEHOLDER;
  const limit = ['author', 'copyrightOwner', 'copyrightSource'].includes(name)
    ? 100
    : isStartPage
    ? 3
    : name === 'title'
    ? 50
    : isPubDate
    ? 10
    : 30;
  const coverRadioValues = [!isNewCover, isNewCover];

  const handleRadio = () => setIsNewCover(!isNewCover);

  const DetailInput = (
    <>
      {name === 'pubDate' ? (
        <Controller
          control={control}
          name={name}
          defaultValue={value}
          render={() => (
            <DatePicker
              className="datepicker-input"
              onChange={(date) => {
                setValue(name, formatDate(date?.toString() ?? ''));
              }}
              selected={
                getValues('pubDate')
                  ? new Date(getValues('pubDate').replaceAll('.', '-'))
                  : null
              }
              dateFormat={
                i18n.language === 'ko-KR' ? 'yyyy.MM.dd' : 'MM/dd/yyyy'
              }
              locale={i18n.language}
              placeholderText={placeholder}
              showPopperArrow={false}
              showDisabledMonthNavigation
            ></DatePicker>
          )}
        ></Controller>
      ) : name !== 'orgCoverYn' ? (
        <div
          className={cx('input_box', {
            lone: hasProfanity,
            'input--error': hasProfanity,
          })}
        >
          <Input
            type="text"
            id={name}
            classnames={[
              'input_t',
              'w100',
              extraInputClass,
              'line',
              hasProfanity ? 'h36' : '',
            ]}
            value={value}
            placeholder={placeholder}
            limit={limit}
            onlyNumber={isPubDate || isStartPage}
            onValidate={(isValid) => setHasProfanity(!isValid)}
          />
        </div>
      ) : (
        <>
          <span className={cx('row')}>
            {coverRadioValues.map((value, i) => (
              <span key={i} className={cx('radio_wrap')}>
                <label>
                  <Input
                    type="radio"
                    id="orgCoverYn"
                    value={+!value}
                    isHidden={true}
                    checked={!!value}
                    onChange={handleRadio}
                  ></Input>
                  <span className={cx('rlabel')}>
                    {i ? NEW : ORIGINAL} {COVER}
                  </span>
                </label>
              </span>
            ))}
          </span>
          <span className={cx('row')}>
            <span className={cx('share_url', { disabled: !isNewCover })}>
              <span className={cx('url', 'text_clip')}>{coverFilename}</span>
              <TooltipWrapper content={BROWSE}>
                {(tooltipProps) => (
                  <label
                    htmlFor="updateCover"
                    className={cx('btn', 'openfile')}
                    {...tooltipProps}
                  >
                    {BROWSE}
                    <Input
                      type="file"
                      id="updateCover"
                      accept="image/png, image/jpeg"
                      onChange={({ target }) => {
                        target.files && setCoverFilename(target.files[0].name);
                      }}
                    />
                  </label>
                )}
              </TooltipWrapper>
            </span>
          </span>
        </>
      )}

      {isStartPage && <TipButton name={name} />}
    </>
  );

  useEffect(() => {
    if (
      isOfType(singleItem, 'project') &&
      isOfType(singleItem.project, 'orgCoverYn')
    ) {
      !singleItem.project.orgCoverYn &&
        setCoverFilename(singleItem.project.coverFileName);
      setIsNewCover(!singleItem.project.orgCoverYn);
    }
  }, [isOfType(singleItem, 'project') && singleItem.project.projectId]);

  return (
    <div className={cx('panel_row', name)}>
      <span className={cx('sub')}>{title}</span>
      <span className={cx('detail', { invalid: hasProfanity })}>
        {DetailInput}
      </span>
    </div>
  );
}
